/**
 * Widgets — the derived, frozen view of Carn that the OS draws outside the app.
 *
 * Authorised by: product-concept §6.2 (the Interrupt Button is "a large, always-reachable
 * control (**lock-screen widget**, watch complication, long-press app icon)"), §7.1 ("hold
 * the widget / watch / earbud and speak"), §6.3 (the resumption surface: the warm loops "each
 * shown *as its capsule*" plus one quiet row), §8.2 (shopping mode and its list), §12 (the
 * free tier names the interrupt button, so a widget is never a paid gate), and
 * implementation-brief §8 ("Swift WidgetKit extension via Expo custom targets, App Group
 * shared storage so a widget tap writes even if the app is cold") and §2 (the device is the
 * source of truth). The working spec for the whole workstream is
 * `docs/specs/widgets-architecture.md`; it has no authority of its own and every rule below
 * traces to one of the three briefs.
 *
 * WHAT THIS MODULE IS. A pure projection: loops, settings, a shopping list, a work session
 * and an instant in, plain serialisable data out. No React, no I/O, no platform imports, no
 * `Date.now()` — `now` is a parameter, exactly as it is everywhere else in `@carn/core`. It
 * assigns no temperature (LAW 2 — `deriveTemperatures` is the only thing that may), it
 * writes nothing, and it is deterministic: the same input produces the same output on a
 * device, in a Netlify Function and in CI.
 *
 * WHAT IT DELIBERATELY DOES NOT HOLD, because a widget is drawn by the OS from a frozen
 * snapshot and every law in CLAUDE.md binds harder on a surface with no scroll, no hover and
 * no way to ask a follow-up question:
 *
 *   1. **No English.** Not one user-visible string is composed here. Titles and item names
 *      are the user's own words passed through; everything else — the label under an icon,
 *      the word for a temperature, the phrase that stands in for a hidden title — is a
 *      structured fact that `packages/copy` renders in the user's register. This is
 *      `docs/DEVIATIONS.md` D10's ruling ("core returns a structured fact, not a sentence")
 *      applied to a second surface, and it is what makes implementation-brief §11's
 *      "literal-language mode covers 100% of shipped strings" true on a home screen.
 *   2. **No countdown, no expiry, no streak, no ageing badge.** `deadlineProminence` is
 *      carried; the deadline's own instant is NOT, so no native reader can subtract it from
 *      the clock and draw "2 days left". Nothing here shrinks with time (CLAUDE.md; the
 *      no-overdue law). The one number that shrinks is the shopping list's `remaining`, and
 *      it shrinks because the user ticks things off, not because time passes.
 *   3. **No compost or cooling COUNT as a widget of its own.** See `WIDGET_KINDS`.
 *   4. **No colour, no size, no duration.** `packages/tokens` resolves those at the user's
 *      Intensity and hands native a flat result; this module carries the non-colour channels
 *      that make design-direction §3's redundancy law satisfiable — a glyph name and the
 *      temperature's own reason — and nothing that looks like a hex value.
 */

import type { Session } from './events';
import { SESSION_STALE_MS } from './events';
import {
  type Capsule,
  type CapsuleImagery,
  type CaptureChannel,
  type Loop,
  type Temperature,
  type TemperatureReason,
  TEMPERATURE_GLYPH,
  currentCapsule,
  deadlineProminence,
  deriveTemperatures,
  orderWarmSet,
  visibleCapsuleFields,
} from './model';
import {
  type CarnList,
  type ListItem,
  type SectionId,
  isBuiltInSection,
  sectionsById,
  remainingCount,
  shoppingModeGroups,
} from './lists';

// ---------------------------------------------------------------------------
// Versioning (docs/specs/widgets-architecture.md §6)
// ---------------------------------------------------------------------------

/**
 * The payload contract between this module and the two native readers.
 *
 * A NATIVE READER THAT DOES NOT KNOW THIS NUMBER RENDERS THE EMPTY STATE — never a partial
 * one, never a guessed one. The failure mode this buys off is specific and reachable: an app
 * downgrade (EAS Update rolls the JS back, or the user reinstalls an older build) leaves a
 * newer payload sitting in App Group storage, and a lenient reader would draw whichever
 * fields it happened to recognise. A widget drawing half a truth is a widget drawing a lie,
 * and the user cannot tell — there is no error state on a home screen, only content.
 *
 * `apps/mobile/src/native/widgets.ts` stamps this onto the envelope as `schemaVersion`
 * (spec §4). It lives here rather than in the bridge because the shape it describes is
 * declared here: bumping the shape and bumping the version have to be one edit, and a
 * constant in another package is a constant that gets forgotten.
 *
 * BUMP IT for any change a reader could misinterpret — a removed field, a re-meaning of an
 * existing one, a widened union whose new member changes what an old member implies. Adding
 * a field an old reader will ignore does not need a bump; if you have to think about whether
 * it does, it does.
 */
export const WIDGET_PAYLOAD_VERSION = 1;

// ---------------------------------------------------------------------------
// The four kinds, and the compile-time proof that there is no fifth
// ---------------------------------------------------------------------------

/**
 * Exactly four widget kinds, for ever.
 *
 * | kind        | what it surfaces                                  | authority     |
 * |-------------|---------------------------------------------------|---------------|
 * | `interrupt` | the Interrupt Button: one press parks             | §6.2, §6.1    |
 * | `resume`    | "where was I?" — warm loops, each as its capsule   | §6.3, §6.1    |
 * | `capture`   | two-second capture, zero decisions                | §7.1          |
 * | `shopping`  | the next unchecked items, and how many are left   | §8.2, §8.1    |
 *
 * **THERE IS NO COMPOST OR COOLING-COUNT WIDGET, AND THERE MUST NOT BE ONE.**
 * product-concept §8.3 is explicit that "the *default visual experience* of Carn never shows
 * the user a wall of their own unmet intentions", and a home screen is the most default
 * surface a phone has — it is what the user sees before they have decided to look at
 * anything. A tile reading "14 loops cooling" pinned there is not a smaller wall, it is the
 * same wall moved somewhere the user cannot choose not to face. The same argument disposes
 * of an inbox/unsorted badge (see `CaptureSnapshot`), and of any "progress" tile: §13's
 * anti-metrics make session count and click-through things we do not optimise, and a number
 * on a home screen is an optimisation target whether or not anyone reads the dashboard.
 *
 * `resume` may carry **one** quiet cooling row, at its largest family and nowhere else —
 * that is §6.3's own "below the warm set, one quiet row", not a fifth kind. See
 * `mayRenderCoolingCount`.
 *
 * A fifth member fails the build on the next line. That is the intended outcome: adding one
 * is a product decision that needs a `docs/DEVIATIONS.md` entry, not an array edit. The
 * pattern is `sharing.ts`'s three-gesture ceiling, for the same reason.
 */
export const WIDGET_KINDS = ['interrupt', 'resume', 'capture', 'shopping'] as const;
export type WidgetKind = (typeof WIDGET_KINDS)[number];

type ExactlyFour<T extends readonly unknown[]> = T['length'] extends 4 ? T : never;
const WIDGET_KIND_CEILING: ExactlyFour<typeof WIDGET_KINDS> = WIDGET_KINDS;
export const WIDGET_KIND_COUNT: 4 = WIDGET_KIND_CEILING.length;

export function isWidgetKind(value: string): value is WidgetKind {
  return (WIDGET_KINDS as readonly string[]).includes(value);
}

// ---------------------------------------------------------------------------
// Families — the platform-neutral size vocabulary
// ---------------------------------------------------------------------------

/**
 * The size vocabulary the preview, the Swift target and the Glance target all share.
 *
 * It is deliberately neither WidgetKit's nor Glance's. WidgetKit says
 * `accessoryInline`/`accessoryCircular`/`accessoryRectangular`/`systemSmall`/`systemMedium`/
 * `systemLarge`; Glance says nothing of the kind and sizes in dp. Naming the vocabulary once,
 * here, is what lets `packages/ui`'s `WidgetPreview` render the same six shapes the two
 * native targets do — which is the only way design-direction §3's "present every screen at
 * both extremes" can be a *process* requirement for a surface that cannot be screenshotted
 * by the review harness in its real host.
 *
 * The first three are accessory families — small, chrome-adjacent, and on iOS they live on
 * the **lock screen**. The last three are home-screen tiles. That split is not cosmetic: it
 * is the input to the privacy rules below, because a lock screen is readable by anyone
 * holding the phone and a home screen is not (spec §5; product-concept §14 names photos of
 * the user's home among the most sensitive data classes).
 */
export const WIDGET_FAMILIES = [
  'inline',
  'circular',
  'rectangular',
  'small',
  'medium',
  'large',
] as const;
export type WidgetFamily = (typeof WIDGET_FAMILIES)[number];

export const WIDGET_SURFACES = ['lock', 'home'] as const;
export type WidgetSurface = (typeof WIDGET_SURFACES)[number];

/**
 * Which surface each family is drawn on. **The privacy rules key off this and nothing else**
 * — see `shouldRedact`.
 *
 * `as const satisfies` is load-bearing rather than stylistic, for the reason
 * `TEMPERATURE_GLYPH` gives: a family with no declared surface must be a BUILD ERROR, not a
 * family that silently defaults to `home` and therefore silently defaults to showing the
 * user's fence photo on their lock screen.
 */
export const WIDGET_FAMILY_SURFACE = {
  inline: 'lock',
  circular: 'lock',
  rectangular: 'lock',
  small: 'home',
  medium: 'home',
  large: 'home',
} as const satisfies Record<WidgetFamily, WidgetSurface>;

/**
 * Which families each kind supports, and why each exclusion is an exclusion.
 *
 * `interrupt` and `capture` take every family: they are one gesture with no content, the
 * whole family is the tap target, and CLAUDE.md's ≥56pt floor makes a larger family strictly
 * better and never worse. product-concept §6.2 wants this control "always-reachable"; there
 * is no size at which that stops being true.
 *
 * `resume` excludes `inline` and `circular`. §6.3's content is a *capsule* — "the photo of
 * the half-done fence, the voice line, 'next: get the 13mm from the jacket'" — and neither
 * family can hold one. An inline row would reduce "where was I?" to a bare line of text,
 * which is precisely the representation product-concept §2 identifies as the thing that
 * makes re-entry cost minutes; shipping it as a widget would be the anti-thesis with rounded
 * corners.
 *
 * `shopping` excludes `circular`. A number in a circle with no room for a word breaks
 * design-direction §3's redundancy law outright — there is no position, icon or label left to
 * carry the meaning — and a remaining-count with no item names is not the list, it is a
 * score.
 */
export const WIDGET_FAMILIES_BY_KIND = {
  interrupt: ['inline', 'circular', 'rectangular', 'small', 'medium', 'large'],
  resume: ['rectangular', 'small', 'medium', 'large'],
  capture: ['inline', 'circular', 'rectangular', 'small', 'medium', 'large'],
  shopping: ['inline', 'rectangular', 'small', 'medium', 'large'],
} as const satisfies Record<WidgetKind, readonly WidgetFamily[]>;

export function supportsFamily(kind: WidgetKind, family: WidgetFamily): boolean {
  return (WIDGET_FAMILIES_BY_KIND[kind] as readonly WidgetFamily[]).includes(family);
}

/**
 * The one family that may draw §6.3's quiet cooling row.
 *
 * §6.3 authorises "below the warm set, one quiet row" and nothing else, and `WIDGET_KINDS`
 * explains at length why a count of stale loops must not become a surface of its own. The
 * resolution is the narrowest one available: the count travels in the snapshot so the
 * largest home-screen tile — the only family with the room to put it *below* the warm set,
 * which is where design-direction §4's fixed vertical grammar requires it — can render the
 * row exactly as the app does. Every smaller family draws the warm set and stops.
 *
 * A lock-screen family is excluded by the same rule, and would be excluded anyway: the count
 * is the one thing on a lock screen that says something about the user rather than about a
 * task.
 */
export const WIDGET_COOLING_ROW_FAMILY: WidgetFamily = 'large';

export function mayRenderCoolingCount(family: WidgetFamily): boolean {
  return family === WIDGET_COOLING_ROW_FAMILY;
}

// ---------------------------------------------------------------------------
// Text and redaction (spec §5) — the lock screen is a public surface
// ---------------------------------------------------------------------------

/**
 * WHAT CLASS OF THING IS HIDDEN. A structured marker, never a sentence.
 *
 * `packages/copy` renders these as `widget.redacted.*` in both registers. Core emitting the
 * English instead would repeat the exact mistake `docs/DEVIATIONS.md` D10 records against
 * `releaseObservation`: strings in the wrong package, in one register, outside the copy-law
 * linter, at a moment the user is looking straight at them.
 *
 * The class matters, which is why this is four members and not one. "Something is hidden" is
 * useless; "a loop's title is hidden here" tells the user what the row is and lets them
 * decide whether to unlock. The redacted rendering must never be an empty string and must
 * never be a censor bar — a blank row on a lock screen reads as a bug, and this audience
 * will conclude the app has lost their work.
 *
 * A closed string union rather than `{ kind: 'hidden-title' }`, because that is how every
 * other closed vocabulary in this package is modelled (`TEMPERATURE_REASONS`,
 * `PROPOSAL_BASES`, `DEFAULT_DEPARTMENTS`) and it carries exactly the same information.
 *
 * THE MEMBERS ARE THE CATALOGUE'S OWN KEY NAMES — `widget.redacted.title`,
 * `widget.redacted.next`, `widget.redacted.item`, `widget.redacted.generic` — which is the
 * confirmation `packages/copy` asks for in a note above that family ("confirm the key names
 * below match the marker enum once it exists; rename in this file rather than growing a
 * second set"). One vocabulary, spelled the same in both packages, so no lookup table has to
 * exist and no lookup table can drift.
 *
 * The catalogue's other two stand-ins, `widget.redacted.photo` and
 * `widget.redacted.voiceNote`, have no marker here on purpose: neither is a TEXT field. A
 * redacted entry drops `photoUri` and keeps `hasPhoto`, so the renderer already knows a
 * picture is being withheld without core naming it twice.
 *
 * `generic` carries the shopping list's own name. There is no `list` member because the
 * catalogue has no `widget.redacted.list`, and inventing a marker with no string behind it
 * would ship a widget that renders nothing where a word should be.
 */
export const WIDGET_REDACTIONS = ['title', 'next', 'item', 'generic'] as const;
export type WidgetRedaction = (typeof WIDGET_REDACTIONS)[number];

/**
 * One user-content string, in the only three states it can be in.
 *
 * `words`    — the user's own words, passed through untouched.
 * `unnamed`  — the loop has no name of its own (`Loop.title` is `null`; RULINGS R27). Copy
 *              resolves the placeholder from `channel` AT RENDER TIME, in the register in
 *              force then — which is the whole of R27: baking "voice note" into an archive,
 *              or into a payload, freezes one register into something implementation-brief
 *              §11 requires literal mode to cover.
 * `redacted` — withheld on this surface. `hides` says what class of thing it was.
 *
 * **`full` IS ABSENT FROM THE REDACTED MEMBER, AND THAT IS THE POINT.** Spec §3's first law
 * is that native code "draws whatever text it is handed and never composes its own", which
 * cuts both ways: a native reader cannot leak a string it was never handed. If `full` were
 * always present and a `redacted: boolean` sat beside it, then every lock-screen payload
 * would contain the user's fence-job titles in plain text and the only thing standing between
 * them and the lock screen would be a Swift `if`. Making the redacted state a member of the
 * union with no `full` field means the un-redacted words cannot be written into a lock-screen
 * payload by a mistake — they are not in the value. That is implementation-brief §3's
 * privacy-invariant posture applied to text: not a rule about what code should do, a shape
 * that cannot express the wrong thing.
 *
 * `hides` is present on all three members so the class of the field is always known and
 * redaction is a total mapping — `redactText` never has to guess what it is hiding.
 */
export type WidgetText =
  | { readonly kind: 'words'; readonly full: string; readonly hides: WidgetRedaction }
  | {
      readonly kind: 'unnamed';
      readonly channel: CaptureChannel;
      readonly hides: WidgetRedaction;
    }
  | { readonly kind: 'redacted'; readonly hides: WidgetRedaction };

function words(full: string, hides: WidgetRedaction): WidgetText {
  return { kind: 'words', full, hides };
}

/** A loop's own words, or the R27 signal that it never had any. */
function loopTitleText(loop: Loop): WidgetText {
  const title = loop.title;
  if (title === null || title.trim() === '') {
    return { kind: 'unnamed', channel: loop.origin.channel, hides: 'title' };
  }
  return words(title, 'title');
}

function redactText(text: WidgetText): WidgetText {
  return { kind: 'redacted', hides: text.hides };
}

/**
 * What the user is willing to have drawn where.
 *
 * `redact-on-lock`  — the default. Loop and list CONTENT is withheld on the lock screen; the
 *                     CONTROLS keep working. This is spec §5's ruling and it is the only
 *                     default that satisfies two requirements at once: the Interrupt Button
 *                     must work without revealing what is being interrupted (§6.2 makes it
 *                     "always-reachable", and a control that first demands a Face ID glance
 *                     is not), while product-concept §14 keeps photos of the user's home off
 *                     a surface anyone holding the phone can read.
 * `show-everywhere` — the user has decided their lock screen is private enough. A real choice
 *                     for a person who lives alone, and refusing to offer it would be the app
 *                     overruling them about their own phone (§11: "Full sensory settings",
 *                     and the same posture about control).
 *
 * **EXACTLY TWO, BECAUSE SPEC §5 AUTHORISES EXACTLY TWO.** A third policy — content withheld
 * on the home screen as well, for a phone handed to children or screen-shared at work — was
 * drafted and cut. It is a plausible product idea and it is not authorised by any of the
 * three briefs: §5's setting is about the lock screen, and `packages/copy`'s
 * `widget.surface.privacy.*` family carries two options and their two explanations. Building
 * a third would have shipped a setting with no string behind it and a product decision no
 * document made. It is a proposal; see `docs/OPEN-QUESTIONS.md`.
 *
 * iOS `privacySensitive()` is applied on top of this by the native target, never instead of
 * it: that API redacts only when the OS thinks the device is locked, and it cannot know that
 * a home-screen tile is being projected onto a meeting-room wall.
 */
export const WIDGET_PRIVACY_POLICIES = ['redact-on-lock', 'show-everywhere'] as const;
export type WidgetPrivacy = (typeof WIDGET_PRIVACY_POLICIES)[number];

export const DEFAULT_WIDGET_PRIVACY: WidgetPrivacy = 'redact-on-lock';

/**
 * Narrow a stored/synced string to a known policy, falling back to the DEFAULT rather than to
 * the permissive end.
 *
 * The boundary function for every layer that holds this as text — a SQLite column, an outbox
 * payload, a settings blob written by a future build. It never throws, and the direction of
 * the fallback is the only interesting decision in it: an unrecognised value must land on
 * `redact-on-lock`, because the failure mode of guessing wrong in the other direction is the
 * user's private life on a lock screen in a pub. Same rule, same reason, as
 * `normaliseNotificationPolicy` forcing a protected class back on.
 */
export function normaliseWidgetPrivacy(value: string | undefined | null): WidgetPrivacy {
  if (value === undefined || value === null) return DEFAULT_WIDGET_PRIVACY;
  return (WIDGET_PRIVACY_POLICIES as readonly string[]).includes(value)
    ? (value as WidgetPrivacy)
    : DEFAULT_WIDGET_PRIVACY;
}

/** Does content have to be withheld on this surface? */
export function shouldRedact(policy: WidgetPrivacy, surface: WidgetSurface): boolean {
  switch (policy) {
    case 'show-everywhere':
      return false;
    case 'redact-on-lock':
      return surface === 'lock';
    default: {
      const unreachable: never = policy;
      return unreachable;
    }
  }
}

/** Convenience for a caller that holds a family rather than a surface. */
export function shouldRedactFamily(policy: WidgetPrivacy, family: WidgetFamily): boolean {
  return shouldRedact(policy, WIDGET_FAMILY_SURFACE[family]);
}

// ---------------------------------------------------------------------------
// The snapshots
// ---------------------------------------------------------------------------

/**
 * `interrupt` — one press, and what that press will do.
 *
 * product-concept §6.2: "One tap says: *life just happened*. Effects: the active Loop is
 * capsuled and parked without ceremony; optionally, the interrupting thing itself is captured
 * as a new Loop." implementation-brief §11 makes it a definition-of-done clause that this
 * happens "in one interaction" from the selected surface.
 *
 * `target` IS THE SAME LOOP `performInterrupt` WOULD PARK, derived by the same rule the
 * in-app control uses (`apps/mobile/src/screens/interrupt/performInterrupt.ts`, and the
 * `interruptTarget` line in `ResumptionSurface.tsx`): the loop being worked if a session is
 * running, otherwise the loop the resumption surface is leading with — which is the app's own
 * answer to "where was I?", and therefore the honest thing for "life happened" to mean on a
 * cold open. The rule is restated here rather than imported because `@carn/core` cannot
 * import from `apps/mobile`; the two are pinned to each other by
 * `packages/core/test/widgets.test.ts` and by the mobile suite.
 *
 * `press` IS DERIVED FROM `target` AND EXISTS SO NATIVE NEVER HAS TO INFER IT. With nothing to
 * put down, the press does not fail and does not disable — it catches the new thing (§6.2's
 * second effect, §7.1's two-second capture). A disabled Interrupt Button is a control that
 * appears and disappears, which design-direction §4 forbids, and it would break §6.2's
 * "always-reachable" on the one surface where reachability was the whole point.
 */
export type InterruptSnapshot = {
  readonly kind: 'interrupt';
  /** Absent when there is nothing open to put down. */
  readonly target?: { readonly loopId: string; readonly title: WidgetText };
  /** What one press does. Derived from `target`, so the two cannot disagree. */
  readonly press: 'park' | 'catch';
  /**
   * Is a work session genuinely running on `target`?
   *
   * Genuinely: a session whose heartbeat has gone quiet for longer than `SESSION_STALE_MS`
   * died with the app and is not running, whatever the stored row says (`recoverSession` in
   * ./events.ts owns that rule). A widget claiming a session is running when the app is dead
   * would offer to bank a stone for work that stopped an hour ago, and the stone log is
   * append-only, so the error would be permanent and uncorrectable.
   */
  readonly sessionRunning: boolean;
};

/**
 * One warm loop, as its capsule (product-concept §6.3).
 *
 * "Re-entry cost drops from minutes of reconstruction to seconds of recognition" — so the
 * fields here are exactly the three things §6.3 names (photo, voice line, next action) plus
 * what a card needs to be legible without colour.
 *
 * `temperature` AND `glyph` AND `reason` TRAVEL TOGETHER, and that is design-direction §3's
 * redundancy law made unavoidable. Inside the app a colour can lean on a label a component
 * happens to render; a widget has no hover, no tooltip and no scroll, so "colour is always
 * redundant with position, icon, or label" can only hold if the icon and the label's key are
 * in the payload. `reason` is there because `cooling` means two incompatible things and the
 * copy layer must branch on it — calling a loop touched four minutes ago cold is a false
 * statement (see `TemperatureReason` in ./model.ts).
 */
export type ResumeEntry = {
  readonly loopId: string;
  readonly title: WidgetText;
  /** The first physical action the capsule stores as "next" (product-concept §9.1). */
  readonly nextAction?: WidgetText;
  /**
   * The local file URI of the capsule photo, when there is one to draw.
   *
   * Dropped by redaction while `hasPhoto` survives, because the two facts are different
   * sensitivities: that a bookmark exists is not private, and the picture of the user's
   * half-disassembled pump in their own kitchen is the class product-concept §14 names first.
   * Absent, too, whenever the user has turned capsule imagery off — that preference is
   * applied through `visibleCapsuleFields`, the same function the in-app card uses, so the
   * two surfaces cannot disagree about it (product-concept §6, objection 3).
   */
  readonly photoUri?: string;
  readonly hasPhoto: boolean;
  /** Presence only. A widget cannot play audio; tapping opens the loop, which can. */
  readonly hasVoiceNote: boolean;
  readonly temperature: Temperature;
  readonly reason: TemperatureReason;
  /** `TEMPERATURE_GLYPH`'s name for this state. The non-colour channel (§3). */
  readonly glyph: string;
  /**
   * 0..1 — importance, never threat (design-direction §3, objection 3).
   *
   * THE DEADLINE'S OWN INSTANT IS DELIBERATELY NOT CARRIED. Prominence is a rendering
   * instruction — draw this one bigger, keep it at the top — and it decays on its own after
   * the date passes (`DEADLINE_GRACE_DAYS`). The instant would let a native reader subtract
   * it from the clock and draw a countdown, and a countdown on a home screen is the overdue
   * state rebuilt out of arithmetic. Red was never the point; the feeling was.
   */
  readonly deadlineProminence: number;
};

/**
 * `resume` — the resumption surface, frozen (product-concept §6.3).
 *
 * `entries` IS NOT TRUNCATED HERE. The warm cap is soft (product-concept §5) and the set
 * `deriveTemperatures` returns already honours it, including the two admissions above it (the
 * active loop, and a hard commitment close enough to earn persistence). Slicing it in core
 * would be a hard cap core does not have, applied where the user cannot see it happening.
 * Each family draws as many rows as it has room for and no more; that is a rendering
 * decision, and it belongs to whoever knows the height.
 */
export type ResumeSnapshot = {
  readonly kind: 'resume';
  /** The warm set, in `orderWarmSet` order. The leading entry is the leading card. */
  readonly entries: readonly ResumeEntry[];
  /**
   * §6.3's one quiet row: "3 loops cooling — look when you're ready". **Only the `large`
   * family may render it** (`mayRenderCoolingCount`).
   *
   * GENUINELY IDLE ONLY — `reason: 'window'`. Loops that are cooling merely because the warm
   * set was full (`reason: 'over-cap'`) are warm-adjacent and are excluded, because a row
   * that calls something the user touched four minutes ago cold is the false statement
   * `TemperatureReason` exists to prevent, and the widget has one row where the app has two.
   * The number therefore matches the app's cooling row exactly; it is a subset of the app's
   * two rows, never a contradiction of them.
   *
   * THERE IS NO COMPOSTING COUNT IN THIS TYPE AND THERE MUST NOT BE ONE (§8.3; see
   * `WIDGET_KINDS`). Compost is reached by going downward, deliberately, in the app.
   */
  readonly coolingCount: number;
};

/**
 * `capture` — the affordance, and nothing else (product-concept §7.1).
 *
 * `offlineReady` IS TYPED AS THE LITERAL `true`, so no code anywhere can file capture as
 * unavailable. That is not decoration: implementation-brief §2 makes the local write the real
 * write and CLAUDE.md makes offline capture a law, so "capture is unavailable" is not a state
 * this product has. A widget that greys itself out because the phone has no signal — in a
 * barn, which is the reference case — would be the app inventing a failure it does not have.
 *
 * **THERE IS NO UNSORTED COUNT, AND CARRYING ONE WAS CONSIDERED AND REFUSED.** The number is
 * available (`inbox()` in ./sorting.ts) and it is exactly the kind of thing a capture widget
 * would traditionally badge. It is not authorised. product-concept §7.2 promises that "if
 * Sorting is skipped for days, nothing breaks", and §8.3's rule against showing "a wall of
 * their own unmet intentions" on the default surface does not become inapplicable because the
 * pile is small and recent — a rising number on the home screen is pressure to triage, which
 * is precisely the obligation §7.2 spent its whole design removing. Preferring to carry
 * nothing over inventing a number is the standing rule; here the number exists and carrying
 * it is still wrong.
 */
export type CaptureSnapshot = {
  readonly kind: 'capture';
  readonly offlineReady: true;
};

export type ShoppingLine = {
  readonly itemId: string;
  readonly name: WidgetText;
  /**
   * The section id — for grouping, and never drawn. R22's law survives R125 intact: ids, never
   * display names.
   */
  readonly section: SectionId;
  /**
   * WHAT THE SECTION IS CALLED, as a finished string — and the reason this is a string rather
   * than a key is R125.
   *
   * Before R125 every section was one of twelve built-ins, so a widget could be handed the id
   * and look up `dept.<id>` in the string table. R125 let the user author their own sections:
   * "Mum" gets a minted id, and there is no catalogue key behind it because the user said the
   * name, not Carn (`packages/ui/src/components/departmentCopy.ts` argues the split at length —
   * a built-in name is COPY, an authored name is DATA).
   *
   * So a native reader composing `"dept." + line.section` — which is what the Kotlin and Swift
   * did — now produces `dept.sec-4f2a` for an authored section, misses, and draws nothing where
   * the user's own word should be. It was also key composition inside native code, which spec §3
   * law 1 forbids outright.
   *
   * The fix is that the name is resolved before it is serialised. Core fills this in for an
   * AUTHORED section, because the label is on the section row and is user data it already holds;
   * it leaves it `undefined` for a BUILT-IN, and the payload composer — which may import
   * `@carn/copy`, as core may not — fills that one from `dept.*` in the user's register. By the
   * time a payload reaches a widget, every line carries a finished word and native composes
   * nothing.
   */
  readonly sectionName?: string;
};

/**
 * `shopping` — the next few unchecked items, and how many are left (product-concept §8.2).
 *
 * ORDER IS `shoppingModeGroups`' ORDER, FLATTENED: the user's own manual department order,
 * then oldest-captured first within a department. Nothing else. **Route learning stays dead**
 * (§8.2's founder decision note) and this module holds no check-off history, no visit record
 * and no per-item position — its output is invariant under any permutation of the items'
 * check timestamps, exactly as `lists.ts` requires of every consumer.
 */
export type ShoppingSnapshot = {
  readonly kind: 'shopping';
  readonly listId?: string;
  readonly listName?: WidgetText;
  /** At most `WIDGET_SHOPPING_ITEM_LIMIT`. A prefix of the live list, never a sample. */
  readonly lines: readonly ShoppingLine[];
  /**
   * How many unchecked items are on the list in total — including the ones `lines` had no
   * room for, so `remaining - lines.length` is the unshown tail.
   *
   * IT SHRINKS, AND THAT IS LEGAL. "Nothing is ever taken away by time" is a law about time:
   * this number falls because the user ticked something off in aisle three, and §8.2's
   * specified behaviour is that "checked items vanish; the list ends". It does not fall on
   * its own, it has no target, and reaching zero is the end of a trip rather than a score.
   */
  readonly remaining: number;
};

/**
 * A handful. Five, for `REVIEW_BATCH_SIZE`'s reason and by the same argument: §8.2's shopping
 * mode is "nothing else on screen", and a widget that tries to be the list is a widget that
 * is unreadable at arm's length with a trolley in the other hand. `remaining` always tells
 * the truth about the total, so nothing is hidden by the ceiling — it is a bound on what the
 * payload carries, not a claim about the list.
 */
export const WIDGET_SHOPPING_ITEM_LIMIT = 5;

export type WidgetSnapshot =
  | InterruptSnapshot
  | ResumeSnapshot
  | CaptureSnapshot
  | ShoppingSnapshot;

/** Every kind's snapshot, keyed by kind. The whole output of one derivation pass. */
export type WidgetSnapshots = {
  readonly [K in WidgetKind]: Extract<WidgetSnapshot, { kind: K }>;
};

// ---------------------------------------------------------------------------
// Derivation — the single entry point
// ---------------------------------------------------------------------------

/**
 * Everything the projection needs, and nothing it does not.
 *
 * The settings fields are named individually rather than taking `Settings` whole, which is
 * the convention every other module in this package follows (`TodayContext`,
 * `DerivationOptions`, `SurfacingContext`) and which here is also a hard requirement:
 * `settings.ts` imports the widget privacy type from this file, so a dependency the other way
 * would be a cycle.
 */
export type WidgetDerivationInput = {
  readonly now: number;
  readonly loops: readonly Loop[];
  /** The soft warm cap (product-concept §5). Passed straight to `deriveTemperatures`. */
  readonly warmCap: number;
  /**
   * A declared storm suspends the cap entirely (§5, objection 2). Read from the stored
   * declaration with `stormActive(settings.storm, now)` — a boolean here, exactly as
   * `DerivationOptions` takes one, because R34 makes the declaration the only stored form.
   */
  readonly stormMode?: boolean;
  /**
   * The running work session, if any. **The only source of "which loop am I on"** — passing
   * an `activeLoopId` beside it would be two fields for one fact, which is the shape R34
   * settled against.
   */
  readonly session?: Session;
  /** Capsule imagery preference (product-concept §6, objection 3). */
  readonly imagery: CapsuleImagery;
  /** The shopping list the widget follows, with its items. Absent = no list to show. */
  readonly shopping?: {
    readonly list: CarnList;
    readonly items: readonly ListItem[];
  };
};

/**
 * Derive every widget's snapshot in one pass.
 *
 * PURE AND DETERMINISTIC. No clock is read, no randomness is drawn, no store is touched: the
 * same input gives the same output every time, which is what product-concept §11's
 * interface-stability promise means on a surface the user cannot refresh. It is also what
 * makes a widget testable at all — the OS decides when to ask for a timeline, and a
 * projection that answered differently on the second ask would be unreviewable.
 *
 * TEMPERATURE IS `deriveTemperatures`' ANSWER AND NOTHING ELSE (LAW 2). One call, its verdicts
 * used for both the warm set and the cooling count, so the widget and the app cannot disagree
 * about a loop the way two independent derivations eventually would.
 *
 * NOTHING HERE IS REDACTED YET. Derivation is surface-agnostic — one pass feeds a lock-screen
 * accessory and a home-screen tile, and `packages/ui`'s preview needs the real words. Apply
 * `redactSnapshots` at the point the payload is written for a surface; that is the only
 * sanctioned projection, and `WidgetText`'s union makes the result structurally incapable of
 * carrying the words it hid.
 */
export function deriveWidgetSnapshots(input: WidgetDerivationInput): WidgetSnapshots {
  const { now, loops, imagery } = input;

  // A session whose heartbeat died with the app is not a running session (./events.ts).
  const running =
    input.session !== undefined && now - input.session.lastHeartbeatAt <= SESSION_STALE_MS;
  const activeLoopId = running ? input.session?.loopId : undefined;

  const verdicts = deriveTemperatures(loops, {
    now,
    warmCap: input.warmCap,
    stormMode: input.stormMode,
    activeLoopId,
  });

  const warm: Loop[] = [];
  let coolingCount = 0;
  for (const loop of loops) {
    // No verdict means a removed loop or a list-bound one (a shopping item). Both are
    // `deriveTemperatures`' answer, and re-deciding either here is how four groceries end up
    // filling the warm set again.
    const verdict = verdicts.get(loop.id);
    if (!verdict) continue;
    // `caught` is warm and is deliberately not an entry (docs/DEVIATIONS.md D44): this is the
    // RESUME widget, and a loop nobody has ever picked up has nothing to resume and no capsule
    // to recognise it by — it would render as a title on a lock screen under a heading asking
    // "Where was I?", which is the false promise D38 removed from the card. It is not counted
    // in `coolingCount` either: that number is a subset of the app's own rows and must not
    // start meaning something the app's rows do not (see the field).
    if (verdict.temperature === 'warm' && verdict.reason !== 'caught') warm.push(loop);
    // `over-cap` is warm-adjacent and is deliberately not counted here — see `coolingCount`.
    else if (verdict.temperature === 'cooling' && verdict.reason === 'window') coolingCount += 1;
  }

  const ordered = orderWarmSet(warm, now, activeLoopId);

  const entries: ResumeEntry[] = ordered.map((loop) => {
    const verdict = verdicts.get(loop.id);
    // `DerivedTemperature` is a branded `Temperature` (LAW 2). Widening a READ is fine and is
    // what every renderer does; minting one is what the brand forbids, and nothing here mints.
    const temperature = (verdict?.temperature ?? 'warm') as Temperature;
    const capsule = visibleCapsule(loop, imagery);
    return {
      loopId: loop.id,
      title: loopTitleText(loop),
      nextAction: capsule?.nextAction ? words(capsule.nextAction, 'next') : undefined,
      photoUri: capsule?.photoUri,
      hasPhoto: capsule?.photoUri !== undefined,
      hasVoiceNote: Boolean(capsule?.voiceNoteUri ?? capsule?.voiceNoteTranscript),
      temperature,
      reason: verdict?.reason ?? 'recent',
      glyph: TEMPERATURE_GLYPH[temperature],
      deadlineProminence: deadlineProminence(loop.date, now),
    };
  });

  // The loop one press puts down: the one being worked, else the one the surface leads with.
  const target = ordered.find((loop) => loop.id === activeLoopId) ?? ordered[0];

  return {
    interrupt: {
      kind: 'interrupt',
      target: target ? { loopId: target.id, title: loopTitleText(target) } : undefined,
      press: target ? 'park' : 'catch',
      sessionRunning: running && target !== undefined && target.id === activeLoopId,
    },
    resume: { kind: 'resume', entries, coolingCount },
    capture: { kind: 'capture', offlineReady: true },
    shopping: deriveShopping(input.shopping),
  };
}

/** The capsule as the user has chosen to see it — photos off is a preference, not a delete. */
function visibleCapsule(loop: Loop, imagery: CapsuleImagery): Capsule | undefined {
  const capsule = currentCapsule(loop);
  return capsule ? visibleCapsuleFields(capsule, imagery) : undefined;
}

function deriveShopping(shopping: WidgetDerivationInput['shopping']): ShoppingSnapshot {
  if (!shopping) return { kind: 'shopping', lines: [], remaining: 0 };
  const { list, items } = shopping;

  // `shoppingModeGroups` is the ONLY ordering: manual department order, then oldest-captured
  // first inside a department. It already drops checked and removed items ("checked items
  // vanish", §8.2), so the flattened prefix is the next few things to pick up.
  const lines: ShoppingLine[] = [];
  // Authored sections only: `isBuiltInSection` is what separates the user's words from Carn's.
  const sections = new Map(
    [...sectionsById(list)].filter(([id]) => !isBuiltInSection(id)),
  );
  for (const group of shoppingModeGroups(list, items)) {
    for (const item of group.items) {
      if (lines.length >= WIDGET_SHOPPING_ITEM_LIMIT) break;
      const authored = sections.get(group.section);
      lines.push({
        itemId: item.id,
        name: words(item.name, 'item'),
        section: group.section,
        // Only an authored section's label travels from here — see `ShoppingLine.sectionName`.
        // A built-in's word is Carn's, so it is copy, so it is the composer's to resolve.
        ...(authored ? { sectionName: authored.label } : null),
      });
    }
    if (lines.length >= WIDGET_SHOPPING_ITEM_LIMIT) break;
  }

  return {
    kind: 'shopping',
    listId: list.id,
    listName: words(list.name, 'generic'),
    lines,
    remaining: remainingCount(items),
  };
}

// ---------------------------------------------------------------------------
// Redaction — the projection for one surface
// ---------------------------------------------------------------------------

/**
 * The snapshots as they may be drawn on `surface`, under `policy`.
 *
 * THE ONLY SANCTIONED PROJECTION. `apps/mobile/src/native/widgets.ts` writes one payload per
 * surface and must run every snapshot through this on the way; a payload assembled straight
 * out of `deriveWidgetSnapshots` carries the user's own words and belongs only in-process
 * (the RN preview in `packages/ui`).
 *
 * WHAT REDACTION DOES NOT TOUCH, on purpose. The controls stay live: `press` still says what
 * one press does, `sessionRunning` is still true, `offlineReady` is still true, `remaining` is
 * still the count, and every structural fact — temperature, glyph, reason, prominence,
 * department, whether a photo or a voice line exists — survives. Spec §5's ruling is that the
 * Interrupt Button "must work without revealing what is being interrupted", so what is
 * withheld is CONTENT (titles, next actions, item names, list names, the photo itself) and
 * never CAPABILITY. A widget that redacted itself into a dead rectangle would have solved the
 * privacy problem by removing the feature.
 */
export function redactSnapshots(
  snapshots: WidgetSnapshots,
  policy: WidgetPrivacy,
  surface: WidgetSurface,
): WidgetSnapshots {
  if (!shouldRedact(policy, surface)) return snapshots;
  return {
    interrupt: redactInterrupt(snapshots.interrupt),
    resume: redactResume(snapshots.resume),
    capture: snapshots.capture,
    shopping: redactShopping(snapshots.shopping),
  };
}

/** One snapshot, same rules. Callers narrow on `.kind`. */
export function redactSnapshot(
  snapshot: WidgetSnapshot,
  policy: WidgetPrivacy,
  surface: WidgetSurface,
): WidgetSnapshot {
  if (!shouldRedact(policy, surface)) return snapshot;
  switch (snapshot.kind) {
    case 'interrupt':
      return redactInterrupt(snapshot);
    case 'resume':
      return redactResume(snapshot);
    case 'capture':
      return snapshot;
    case 'shopping':
      return redactShopping(snapshot);
    default: {
      const unreachable: never = snapshot;
      return unreachable;
    }
  }
}

function redactInterrupt(snapshot: InterruptSnapshot): InterruptSnapshot {
  if (!snapshot.target) return snapshot;
  return {
    ...snapshot,
    target: { loopId: snapshot.target.loopId, title: redactText(snapshot.target.title) },
  };
}

function redactResume(snapshot: ResumeSnapshot): ResumeSnapshot {
  return {
    ...snapshot,
    entries: snapshot.entries.map((entry) => ({
      ...entry,
      title: redactText(entry.title),
      nextAction: entry.nextAction ? redactText(entry.nextAction) : undefined,
      // The picture goes; the fact that a bookmark exists stays. See `ResumeEntry.photoUri`.
      photoUri: undefined,
    })),
  };
}

function redactShopping(snapshot: ShoppingSnapshot): ShoppingSnapshot {
  return {
    ...snapshot,
    listName: snapshot.listName ? redactText(snapshot.listName) : undefined,
    lines: snapshot.lines.map((line) => ({ ...line, name: redactText(line.name) })),
  };
}
